import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DrawerFooter } from "@/components/ui/drawer";
import { AddFieldProps } from "./types";
import { ADD_CUSTOM_FIELD_TRANSLATION_KEY } from "./const";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useFieldForm } from "./hooks/useFieldForm";
import { FieldFormContent } from "./components/FieldFormContent";
import { APIFieldItem } from "@/pages/settings/types";
import { WorkflowRequiredFieldSection } from "../StageStudio/workflow/components/WorkflowRequiredFieldSection";

interface AddPresetFieldEditProps extends AddFieldProps {
  presetField: APIFieldItem;
  showWorkflowSection?: boolean;
}

export const AddPresetFieldEdit: React.FC<AddPresetFieldEditProps> = ({
  onBack,
  onCancel,
  onSave,
  presetField,
  showWorkflowSection = false
}) => {
  const { t } = useTranslation();
  const [isRequiredForPass, setIsRequiredForPass] = useState(false);

  const {
    formState,
    currentFieldData,
    isFormValid,
    formRef,
    formSetValue,
    setFormSetValue,
    setShowHelpText,
    handleFormValid,
    handleOptionsManagerChange,
    handleFilledByDreamerChange,
    handleFormSubmit,
    handleSubmitButton,
    isSubmitting
  } = useFieldForm({
    fieldData: presetField,
    entityType: EntityType.DEAL,
    isEditMode: true,
    isPresetFieldEdit: true
  });

  const handleSave = async () => {
    if (isSubmitting) {
      return;
    }

    try {
      const result = await handleFormSubmit();
      // Add workflow-specific data if workflow section is shown
      const finalResult = showWorkflowSection
        ? { ...result, isRequiredForPass }
        : result;
      onSave(finalResult);
    } catch (error) {
      console.error("Failed to update preset field:", error);
    }
  };

  return (
    <>
      <FieldFormContent
        formData={formState.formData}
        optionsManagerData={formState.optionsManagerData}
        fieldData={currentFieldData}
        formSetValue={formSetValue}
        showHelpText={formState.showHelpText}
        showInCreation={false}
        isRequired={false}
        onShowHelpTextChange={setShowHelpText}
        onShowInCreationChange={() => {}}
        onRequiredChange={() => {}}
        onOptionsManagerChange={handleOptionsManagerChange}
        onFilledByDreamerChange={handleFilledByDreamerChange}
        onFormValid={handleFormValid}
        onSubmit={handleSave}
        formRef={formRef}
        onSetValue={setFormSetValue}
        isEditMode={true}
        isPresetFieldEdit={true}
      />

      {showWorkflowSection && (
        <WorkflowRequiredFieldSection
          isRequiredForPass={isRequiredForPass}
          onRequiredForPassChange={setIsRequiredForPass}
          className="px-5"
          translationPrefix="settings.deals.workflow.requiredFieldSection"
        />
      )}

      <DrawerFooter>
        <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
          <Button variant="tertiary" onClick={onBack} className="h-9">
            {t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.back`)}
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button variant="secondaryNoBg" onClick={onCancel} className="h-9">
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSubmitButton}
              disabled={!isFormValid || isSubmitting}
              className="h-9"
            >
              {t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addField`)}
            </Button>
          </div>
        </div>
      </DrawerFooter>
    </>
  );
};
